import React from 'react';
import { Link } from 'react-router-dom';

import { type Location, locationUserPermission } from '@cobliteam/common-types';

import { Analytics } from '@cobliteam/analytics';

import {
  MenuItem,
  MenuItemType,
  PermissionsGate,
} from '@cobliteam/base-components';

import cn from '@cobliteam/ui/utils/cn';

export interface SidebarItemProps {
  children: string | React.ReactNode;
  sidebarOpen: boolean;
  icon: React.ReactNode;
  location: Location;
  active: boolean;
  badge?: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  dataTest?: string;
}

export const SidebarItem = ({
  children,
  sidebarOpen,
  icon,
  location,
  active,
  badge,
  disabled,
  onClick,
  dataTest,
}: SidebarItemProps) => {
  const [hover, setHover] = React.useState(false);

  const isActive = hover || active;

  const handleClick = () => {
    Analytics.track('Sidebar Item Clicked', { page: location });

    if (onClick) {
      onClick();
    }
  };

  return (
    <PermissionsGate anyOfPermissions={locationUserPermission[location]}>
      <Link
        to={location}
        className={cn(
          'flex h-12 w-full cursor-pointer items-center gap-2.5 whitespace-nowrap pl-5 pr-2.5 hover:text-white',
          isActive ? 'bg-secondary-dark' : 'bg-none',
          sidebarOpen ? 'justify-between' : 'justify-center p-0',
          active || disabled ? 'pointer-events-none' : ''
        )}
        onMouseOver={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={handleClick}
        data-test={dataTest}
      >
        <div
          id={dataTest}
          className={cn(
            'flex items-center gap-2.5',
            sidebarOpen ? '' : 'w-full justify-center'
          )}
        >
          <div className={cn('[&>svg]:size-4', isActive ? 'text-white' : '')}>
            {icon}
          </div>

          {sidebarOpen ? (
            <MenuItem type={MenuItemType.LIGHT} active={isActive}>
              {children}
            </MenuItem>
          ) : null}
        </div>

        {sidebarOpen ? badge : null}
      </Link>
    </PermissionsGate>
  );
};
